/**
 * Habitat Page - Agent's Personal Home
 * Stats, Games, Achievements, Consumption Dashboard
 */

import { Activity,        Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { useToast } from '../hooks/useToast'
import api from '../services/api'

interface HabitatData {
  agent_id: number
  level: number
  xp: number
  eq_empati: number
  eq_isbirligi: number
  achievements: Array<{ achievement_name: string; xp_earned: number; created_at: string }>
  available_activities: Array<{ id: number; activity_type: string; difficulty: number; duration_minutes: number; reward_xp: number; topic: string }>
}

export default function HabitatPage(): JSX.Element {
  const { agentId = '13' } = useParams()
  const toast = useToast()
  const [habitat, setHabitat] = useState<HabitatData | null>(null)
  const [loading, setLoading] = useState(true)
  const [playing, setPlaying] = useState<number | null>(null)
  // energy state removed - unused

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/api/habitat/${agentId}`)
        if (res.data && res.data.agent_id) {
          setHabitat(res.data as HabitatData)
        } else {
          throw new Error('Data eksik')
        }
      } catch {
        toast.error('Habitat verisi bulunamadı, demo modunda gösteriliyor.')
        // ASLA BEYAZ EKRAN VERMEMESİ İÇİN GÜVENLİ FALLBACK (DEMO DATA)
        setHabitat({
          agent_id: Number(agentId) || 13,
          level: 4,
          xp: 1450,
          eq_empati: 8,
          eq_isbirligi: 7,
          achievements: [
            { achievement_name: "İlk Fikir", xp_earned: 50, created_at: new Date().toISOString() },
            { achievement_name: "Usta Mantar Onayı", xp_earned: 150, created_at: new Date().toISOString() },
            { achievement_name: "Derin Tartışma", xp_earned: 300, created_at: new Date().toISOString() }
          ],
          available_activities: [
            { id: 1, activity_type: 'quiz', difficulty: 2, duration_minutes: 15, reward_xp: 100, topic: 'Kuantum Fiziği' },
            { id: 2, activity_type: 'puzzle', difficulty: 3, duration_minutes: 30, reward_xp: 250, topic: 'Python Optimizasyon' },
            { id: 3, activity_type: 'debate', difficulty: 4, duration_minutes: 45, reward_xp: 400, topic: 'Yapay Zeka Etiği' }
          ]
        })
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [agentId, toast])

  const startActivity = async (activityId: number, type: string) => {
    try {
      setPlaying(activityId)
      await api.post('/api/habitat/activity/start', {
        agent_id: Number(agentId),
        activity_type: type
      })
    } catch {
      toast.error('Activity start failed')
      setPlaying(null)
    }
  }


  if (loading) {
    return <MainLayout><div style={{ padding: '20px' }}>Yükleniyor...</div></MainLayout>
  }

  if (!habitat) {return <MainLayout><div>Habitat bulunamadı</div></MainLayout>}

  const xpPercent = (habitat.xp / 2000) * 100

  return (
    <MainLayout>
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header: Stats */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '32px' }}>Flz Habitat</h1>
              <p style={{ margin: '5px 0 0', opacity: 0.9 }}>Agent #{habitat.agent_id}</p>
            </div>
            <div style={{ fontSize: '48px', fontWeight: 'bold' }}>Level {habitat.level}</div>
          </div>

          {/* XP Bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Experience</span>
              <span>{habitat.xp} / 2000 XP</span>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              height: '8px',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.9)',
                height: '100%',
                width: `${xpPercent}%`,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>

          {/* EQ Scores */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px' }}>
            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>Empati</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{'⭐'.repeat(habitat.eq_empati)}</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '10px', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>İşbirliği</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{'⭐'.repeat(habitat.eq_isbirligi)}</div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '30px' }}>
          <div style={{ background: '#f0f4ff', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #667eea' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>⚡ Enerji</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>65 / 100</div>
          </div>
          <div style={{ background: '#f0fff4', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>💰 Compute Credits</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>420 / 1000</div>
          </div>
          <div style={{ background: '#fff5f0', padding: '15px', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>🏆 Streak</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>7 gün</div>
          </div>
        </div>

        {/* Activities */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={20} /> Oyunlar & Aktiviteler
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
            {habitat.available_activities.map(act => (
              <div key={act.id} style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '15px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: playing === act.id ? 0.6 : 1
              }} onClick={() => { void startActivity(act.id, act.activity_type); }}>
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>
                  {act.activity_type.toUpperCase()} • Difficulty {act.difficulty}
                </div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                  {act.topic}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                  <span>⏱ {act.duration_minutes}min</span>
                  <span>⭐ +{act.reward_xp} XP</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Result Modal */}
        {/* Achievements */}
        <div>
          <h2 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Trophy size={20} /> Son Başarılar
          </h2>
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '15px' }}>
            {habitat.achievements.length === 0 ? (
              <p style={{ color: '#999' }}>Henüz başarı yok. Oyunları oynamaya başla!</p>
            ) : (
              habitat.achievements.slice(0, 5).map((ach, i) => (
                <div key={i} style={{ padding: '10px 0', borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}>
                  ✓ {ach.achievement_name} (+{ach.xp_earned} XP)
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
